import { useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, BarChart3 } from 'lucide-react';
import { PageHeader, StatsCards, Button, StatCardSkeleton, TabSelector } from '../../common';
import RespondentsPanel, { RespondentSelection } from './components/RespondentsPanel';
import RespondentAnswers from './components/RespondentAnswers';
import SurveySummaryTab from './components/SurveySummaryTab';
import { INSIGHTS_STAT_CARDS } from './utils';
import { useSurveyInsights } from './hooks/useSurveyInsights';
import { formatDDMMYYYY } from '../../../utils/timeUtils';
import { scrollToTop } from '../hooks';

interface InsightsLocationState {
  title?: string;
  endDate?: string;
  audience?: string;
}

/** Figma Survey Results: header + stats + individual responses (dc.html:2944). */
const SurveyInsights: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state || {}) as InsightsLocationState;

  const { stats, loading, error, refresh } = useSurveyInsights(surveyId || '');
  const [selection, setSelection] = useState<RespondentSelection | null>(null);
  const [activeView, setActiveView] = useState<'summary' | 'individual'>('summary');

  const viewTabs = [
    { k: 'summary' as const, label: 'Summary' },
    { k: 'individual' as const, label: 'Individual Responses' },
  ];

  const handleBack = () => {
    navigate('/survey-responses');
    scrollToTop();
  };

  const title = navState.title || 'Survey Results';
  const closedOn = formatDDMMYYYY(navState.endDate) ?? '—';
  const audience = navState.audience || '—';

  const headerStats = useMemo(
    () => ({
      completed: stats.completed,
      completionRate: stats.responseRate ?? stats.completionRate,
      totalAssigned: stats.totalAssigned,
      closedOn,
    }),
    [stats.completed, stats.responseRate, stats.completionRate, stats.totalAssigned, closedOn]
  );

  if (!surveyId) {
    return (
      <div className="w-full">
        <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] text-center px-6 py-14">
          <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">Survey not found</h3>
          <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">
            A valid survey is required to view insights.
          </p>
          <Button appearance="secondary" size="small" onClick={handleBack}>
            Back to Responses
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <PageHeader
        title={loading ? 'Loading…' : title}
        description={`Closed ${closedOn} · ${audience}`}
        breadcrumbs={[
          { label: 'Home', path: '/dashboard' },
          { label: 'Survey Insights', path: '/survey-responses' },
          { label: 'Results' },
        ]}
        actions={
          <Button appearance="secondary" size="middle" onClick={handleBack} icon={<ArrowLeft className="w-4 h-4" />}>
            All surveys
          </Button>
        }
      />

      {error ? (
        <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] text-center px-6 py-14">
          <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">Failed to load insights</h3>
          <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">{error}</p>
          <Button appearance="primary" size="small" onClick={() => refresh()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div className="mb-5 sm:mb-6">
            {loading ? (
              <StatCardSkeleton count={4} />
            ) : (
              <StatsCards
                stats={headerStats}
                cards={INSIGHTS_STAT_CARDS}
                variant="simple-icon"
                valueOnTop
              />
            )}
          </div>

          <div className="mb-5 sm:mb-6">
            <TabSelector
              tabs={viewTabs}
              active={activeView}
              onChange={setActiveView}
            />
          </div>

          {activeView === 'summary' ? (
            <SurveySummaryTab surveyId={surveyId} />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 sm:gap-5 items-start">
              <RespondentsPanel
                surveyId={surveyId}
                selected={selection}
                onSelect={setSelection}
              />
              <RespondentAnswers selection={selection} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SurveyInsights;
